import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Button,
  Field,
  FieldLabel,
  Input,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Textarea,
} from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';
import { DeleteConfirmationModal } from '@/shared/ui';
import { ROUTES } from '@/shared/config/routes';
import { useCreateFolder, useDeleteFolder, useUpdateFolder } from '@/features/guidelines/hooks';
import type { FolderFormSheetProps } from './FolderFormSheet.types';

/**
 * Right-side counterpart to the legacy centre `CreateFolderModal` /
 * `EditFolderModal`, reusing the same mutation hooks so the two surfaces
 * never drift on validation or submit behaviour. Delete navigates straight
 * back to the bare folders route on success, since both the workspace and
 * a specific folder route resolve to the same place once the folder is gone.
 */
export function FolderFormSheet({ open, mode, folder, onClose }: FolderFormSheetProps) {
  const { t } = useTranslation('knowledge');
  const navigate = useNavigate();

  const isEdit = mode === 'edit';

  const [title, setTitle] = useState(folder?.title ?? '');
  const [description, setDescription] = useState(folder?.description ?? '');
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const { mutate: createFolder, isPending: isCreating } = useCreateFolder();
  const { mutate: updateFolder, isPending: isUpdating } = useUpdateFolder();
  const { mutate: deleteFolder, isPending: isDeleting } = useDeleteFolder();

  const isSubmitting = isCreating || isUpdating;
  const isFormValid = title.trim().length > 0 && description.trim().length > 0;

  const sheetTitle = isEdit
    ? t('guidelines.editFolderModal.editGuidelineFolder')
    : t('guidelines.folderFormSheet.createTitle');
  const sheetDescription = isEdit
    ? t('guidelines.folderFormSheet.editDescription')
    : t('guidelines.folderFormSheet.createDescription');

  const handleSubmit = () => {
    if (!isFormValid || isSubmitting) return;
    const trimmed = { title: title.trim(), description: description.trim() };

    if (isEdit && folder) {
      updateFolder({ folderId: folder.id, ...trimmed }, { onSuccess: onClose });
    } else {
      createFolder(trimmed, { onSuccess: onClose });
    }
  };

  const handleConfirmDelete = () => {
    if (!folder || isDeleting) return;
    deleteFolder(
      { folderId: folder.id, folderTitle: folder.title },
      {
        onSuccess: () => {
          setIsDeleteConfirmOpen(false);
          onClose();
          void navigate(ROUTES.KNOWLEDGE_HUB_VISUAL_GUIDELINES);
        },
      },
    );
  };

  return (
    <>
      <Sheet open={open && !isDeleteConfirmOpen} onOpenChange={(next) => !next && onClose()}>
        <SheetContent className="w-[480px] rounded-l-lg p-xl sm:max-w-none">
          <SheetHeader className="p-0">
            <SheetTitle>{sheetTitle}</SheetTitle>
            <SheetDescription>{sheetDescription}</SheetDescription>
          </SheetHeader>
          <div className="-m-1.5 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-1.5">
            <Field>
              <FieldLabel htmlFor="folder-form-title">
                {t('guidelines.createFolderModal.name')}
              </FieldLabel>
              <Input
                id="folder-form-title"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                disabled={isSubmitting}
                maxLength={100}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="folder-form-description">
                {t('guidelines.createFolderModal.description')}
              </FieldLabel>
              <Textarea
                id="folder-form-description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                disabled={isSubmitting}
                maxLength={500}
                rows={4}
              />
            </Field>
          </div>
          <SheetFooter
            data-testid="folder-form-sheet-footer"
            className="flex-row items-center justify-between p-0"
          >
            {isEdit && (
              <Button variant="ghost" size="lg" onClick={() => setIsDeleteConfirmOpen(true)}>
                {t('guidelines.editFolderModal.delete')}
              </Button>
            )}
            <div className="ml-auto flex gap-2">
              <Button variant="outline" size="lg" onClick={onClose} disabled={isSubmitting}>
                {t('guidelines.editFolderModal.cancel')}
              </Button>
              <Button
                variant="default"
                size="lg"
                onClick={handleSubmit}
                disabled={!isFormValid || isSubmitting}
              >
                {isSubmitting
                  ? t('guidelines.editFolderModal.saving')
                  : isEdit
                    ? t('guidelines.editFolderModal.saveChanges')
                    : t('guidelines.createFolderModal.createFolder')}
              </Button>
            </div>
          </SheetFooter>
        </SheetContent>
      </Sheet>
      {folder && (
        <DeleteConfirmationModal
          open={isDeleteConfirmOpen}
          isDeleting={isDeleting}
          onCancel={() => setIsDeleteConfirmOpen(false)}
          onConfirmDelete={handleConfirmDelete}
          resourceType={t('guidelines.editFolderModal.guidelineFolder')}
          resourceName={folder.title}
          warningMessage={t('guidelines.editFolderModal.thisActionCannotBeUndone')}
        />
      )}
    </>
  );
}

FolderFormSheet.displayName = 'FolderFormSheet';
